import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  Switch,
} from '@digdir/designsystemet-react';
import { getCountryOptions, type BaseSchedulingSettings } from '../../api/types/scheduling-settings.types';
import type { SchedulingSettingsFormValues } from '../../components/SchedulingSettingsForm/SchedulingSettingsForm.schema';
import { validateFormValues } from '../../components/SchedulingSettingsForm/formHandlers';
import { SettingsSection } from '../../components/TeamSettingsLayout';
import classes from './WorkingHoursForm.module.css';

/** Det skjemaet trenger fra en mutasjon, uansett hva den returnerer */
type Mutation<TVariables> = {
  mutate: (variables: TVariables) => void;
  isPending: boolean;
  error: Error | null;
};

type WorkingHoursFormProps = {
  effectiveSettings: BaseSchedulingSettings;
  saveMutation: Mutation<SchedulingSettingsFormValues>;
  /** Teksten under overskriften. Standard er teksten for team. */
  description?: string;
  /** Bare for team: tilbake til organisasjonens arbeidstid */
  hasCustomSettings?: boolean;
  deleteMutation?: Mutation<void>;
};

const pad = (value: number) => value.toString().padStart(2, '0');

export const toTimeString = (hour: number, minute: number) => `${pad(hour)}:${pad(minute)}`;

const parseTime = (value: string) => {
  const [hour, minute] = value.split(':').map((part) => parseInt(part, 10));
  return { hour: Number.isNaN(hour) ? 0 : hour, minute: Number.isNaN(minute) ? 0 : minute };
};

const toFormValues = (settings: BaseSchedulingSettings): SchedulingSettingsFormValues => ({
  workdayStartHour: settings.workdayStartHour,
  workdayStartMinute: settings.workdayStartMinute,
  workdayEndHour: settings.workdayEndHour,
  workdayEndMinute: settings.workdayEndMinute,
  skipWeekends: settings.skipWeekends,
  skipHolidays: settings.skipHolidays,
  holidayCountryCode: settings.holidayCountryCode,
});

/** Arbeidstid for team eller organisasjon: tider, helg og helligdager */
export const WorkingHoursForm = ({
  effectiveSettings,
  saveMutation,
  description,
  hasCustomSettings = false,
  deleteMutation,
}: WorkingHoursFormProps) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(() => toFormValues(effectiveSettings));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isResetOpen, setIsResetOpen] = useState(false);

  useEffect(() => {
    setValues(toFormValues(effectiveSettings));
  }, [effectiveSettings]);

  const update = (changes: Partial<SchedulingSettingsFormValues>) => {
    setValidationError(null);
    setValues((prev) => ({ ...prev, ...changes }));
  };

  const onSave = (event: FormEvent) => {
    event.preventDefault();
    const error = validateFormValues(values, t);
    if (error) {
      setValidationError(error);
      return;
    }
    saveMutation.mutate(values);
  };

  const onReset = () => {
    setIsResetOpen(false);
    setValidationError(null);
    deleteMutation?.mutate();
  };

  const isPending = saveMutation.isPending || (deleteMutation?.isPending ?? false);
  const mutationError = saveMutation.error || deleteMutation?.error;
  const start = toTimeString(values.workdayStartHour, values.workdayStartMinute);
  const end = toTimeString(values.workdayEndHour, values.workdayEndMinute);
  const countryName =
    getCountryOptions().find((country) => country.code === values.holidayCountryCode)?.name ??
    values.holidayCountryCode;

  const summary = t('teamSettings.hours.summary', {
    days: values.skipWeekends ? t('teamSettings.hours.weekdays') : t('teamSettings.hours.allDays'),
    start,
    end,
    holidays: values.skipHolidays
      ? t('teamSettings.hours.summaryHolidays', { country: countryName })
      : '',
  });

  return (
    <>
      <SettingsSection
        title={t('teamSettings.hours.title')}
        description={description ?? t('teamSettings.hours.description')}
      >
        <form className={classes.form} onSubmit={onSave} noValidate>
          <div className={classes.times}>
            <Field>
              <Label htmlFor="workday-start">{t('teamSettings.hours.from')}</Label>
              <Input
                id="workday-start"
                type="time"
                className={classes.timeInput}
                value={start}
                disabled={isPending}
                onChange={(e) => {
                  const { hour, minute } = parseTime(e.target.value);
                  update({ workdayStartHour: hour, workdayStartMinute: minute });
                }}
              />
            </Field>
            <Field>
              <Label htmlFor="workday-end">{t('teamSettings.hours.to')}</Label>
              <Input
                id="workday-end"
                type="time"
                className={classes.timeInput}
                value={end}
                disabled={isPending}
                onChange={(e) => {
                  const { hour, minute } = parseTime(e.target.value);
                  update({ workdayEndHour: hour, workdayEndMinute: minute });
                }}
              />
            </Field>
          </div>

          <Fieldset className={classes.fieldset}>
            <Fieldset.Legend>{t('teamSettings.hours.weekendHolidays')}</Fieldset.Legend>
            <div className={classes.switchRow}>
              <Switch
                label={t('teamSettings.hours.skipWeekends')}
                position="end"
                checked={values.skipWeekends}
                disabled={isPending}
                onChange={(e) => update({ skipWeekends: e.target.checked })}
              />
            </div>
            <div className={classes.switchRow}>
              <Switch
                label={t('teamSettings.hours.skipHolidays')}
                position="end"
                checked={values.skipHolidays}
                disabled={isPending}
                onChange={(e) => update({ skipHolidays: e.target.checked })}
              />
            </div>
          </Fieldset>

          {values.skipHolidays && (
            <Field className={classes.countryField}>
              <Label htmlFor="holiday-country">{t('teamSettings.hours.country')}</Label>
              <Select
                id="holiday-country"
                value={values.holidayCountryCode}
                disabled={isPending}
                onChange={(e) => update({ holidayCountryCode: e.target.value })}
              >
                {getCountryOptions().map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    {country.name}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          )}

          <Paragraph data-size="sm" className={classes.summary} aria-live="polite">
            {summary}
          </Paragraph>

          {validationError && (
            <Alert data-color="danger">
              <Paragraph>{validationError}</Paragraph>
            </Alert>
          )}
          {mutationError && (
            <Alert data-color="danger">
              <Paragraph>{mutationError.message || t('scheduling.form.messages.saveFailed')}</Paragraph>
            </Alert>
          )}

          <div className={classes.actions}>
            <Button type="submit" disabled={isPending}>
              {saveMutation.isPending ? t('teamSettings.common.saving') : t('teamSettings.common.save')}
            </Button>
            {hasCustomSettings && deleteMutation && (
              <Button variant="tertiary" onClick={() => setIsResetOpen(true)} disabled={isPending}>
                {deleteMutation.isPending ? t('teamSettings.hours.resetting') : t('teamSettings.hours.reset')}
              </Button>
            )}
          </div>
        </form>
      </SettingsSection>

      <Dialog
        open={isResetOpen}
        onClose={() => setIsResetOpen(false)}
        closedby="any"
        closeButton={false}
        aria-labelledby="reset-hours-heading"
        className={classes.dialog}
      >
        <div className={classes.dialogContent}>
          <Heading id="reset-hours-heading" level={2} data-size="xs">
            {t('teamSettings.hours.reset')}
          </Heading>
          <Paragraph>{t('scheduling.form.messages.confirmDelete')}</Paragraph>
          <div className={classes.actions}>
            <Button onClick={onReset}>{t('teamSettings.hours.reset')}</Button>
            <Button variant="tertiary" onClick={() => setIsResetOpen(false)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
